<template>
  <div class="reader-debug">
    <el-alert
      v-if="debugging"
      class="reader-debug__alert"
      type="warning"
      :closable="false"
      :title="$t('console.reader.activeHint')"
    />
    <div class="reader-debug__bar">
      <el-button v-if="!debugging" type="warning" size="small" @click="startDebug">{{ $t("console.reader.start") }}</el-button>
      <el-button v-else type="danger" size="small" @click="stopDebug">{{ $t("console.reader.stop") }}</el-button>
      <el-tag :type="ready ? 'success' : 'info'" size="small">{{ statusText }}</el-tag>
      <span v-if="lastUid" class="text-muted">{{ $t("console.reader.lastUid") }}：{{ lastUid }}</span>
      <el-button size="mini" :disabled="!ready" @click="send('INFO')">INFO</el-button>
      <el-button size="mini" :disabled="!ready" @click="send('MODE SCAN')">{{ $t("console.reader.scan") }}</el-button>
      <el-button size="mini" @click="lines = []">{{ $t("console.reader.clear") }}</el-button>
    </div>
    <div v-if="debugging" class="reader-debug__send">
      <el-input
        v-model="cardpass"
        size="small"
        maxlength="16"
        :placeholder="$t('console.reader.cardpass')"
      />
      <el-button type="primary" size="small" :disabled="!ready || reading" @click="readCard">{{ $t("console.reader.readCard") }}</el-button>
    </div>
    <div v-if="cardInfo" class="reader-debug__card">
      <div class="reader-debug__index-title">{{ $t("console.reader.indexTitle") }}</div>
      <div class="text-muted">{{ $t("console.reader.indexRaw") }}：{{ cardInfo.indexHex || "-" }}</div>
      <div v-if="cardInfo.indexText">{{ $t("console.reader.indexText") }}：{{ cardInfo.indexText }}</div>
      <div v-if="cardInfo.status === 'blank'" class="reader-debug__empty">{{ $t("console.reader.indexEmpty") }}</div>
      <el-table v-else-if="cardInfo.index" :data="cardInfo.index" size="mini" border>
        <el-table-column :label="$t('console.reader.field')" min-width="120">
          <template slot-scope="scope">{{ fieldLabel(scope.row.field) }}</template>
        </el-table-column>
        <el-table-column prop="sector" :label="$t('console.reader.sector')" width="80" />
        <el-table-column prop="block" :label="$t('console.reader.block')" width="80" />
        <el-table-column :label="$t('console.reader.value')" min-width="140">
          <template slot-scope="scope">{{ displayValue(scope.row) }}</template>
        </el-table-column>
        <el-table-column prop="hex" label="HEX" min-width="220" />
      </el-table>
    </div>
    <div class="reader-debug__send">
      <el-input
        v-model="command"
        size="small"
        :placeholder="$t('console.reader.placeholder')"
        @keyup.enter.native="send()"
      />
      <el-button type="primary" size="small" :disabled="!ready" @click="send()">{{ $t("console.reader.send") }}</el-button>
    </div>
    <pre class="reader-debug__log">{{ logText }}</pre>
  </div>
</template>

<script>
import { sendMessage } from "@/websocket";
import { cardReaderStatus } from "@/utils/cardReaderStatus";
import { parseReadSecLine, parseUidLine } from "@/utils/rfidProtocol";
import { readerDebugLock } from "@/utils/readerDebug";
import { hexToCardText, parseStoredIndex, readMemberCard } from "@/utils/memberCardSector";

export default {
  name: "CardReaderDebug",
  data() {
    return {
      command: "",
      lines: [],
      lastUid: "",
      cardpass: "",
      reading: false,
      cardInfo: null,
    };
  },
  computed: {
    ready() {
      return cardReaderStatus.state === "connected";
    },
    debugging() {
      return readerDebugLock.active;
    },
    statusText() {
      return this.$t("cardreader.status." + cardReaderStatus.state);
    },
    logText() {
      return this.lines.length ? this.lines.join("\n") : this.$t("console.reader.empty");
    },
  },
  created() {
    this.$bus.$on("rfid-line", this.onLine);
  },
  beforeDestroy() {
    this.$bus.$off("rfid-line", this.onLine);
    readerDebugLock.active = false;
  },
  methods: {
    startDebug() {
      readerDebugLock.active = true;
    },
    stopDebug() {
      readerDebugLock.active = false;
    },
    fieldLabel(field) {
      return this.$t("console.reader.fields." + field);
    },
    displayValue(row) {
      if (row.field !== "balance") return row.text || "-";
      return this.cardInfo.balance + (this.cardInfo.currency ? " " + this.cardInfo.currency : "");
    },
    readCard() {
      const key = String(this.cardpass || "").trim();
      if (!key || key.length > 16 || /\s/.test(key)) {
        this.$message.warning(this.$t("console.reader.cardpassInvalid"));
        return;
      }
      this.reading = true;
      this.cardInfo = null;
      readMemberCard(key).then((result) => {
        this.reading = false;
        if (result.status === "unread") {
          this.cardInfo = null;
          this.$message.warning(this.$t("menuitems.clouds.nfcreader.rfid.verifyUnread"));
        } else {
          this.cardInfo = result;
        }
        this.send("MODE SCAN");
      });
    },
    onLine(line) {
      const stamp = new Date().toLocaleTimeString();
      this.lines.push(stamp + "  " + line);
      if (this.lines.length > 80) this.lines.splice(0, this.lines.length - 80);
      const uid = parseUidLine(line);
      if (uid) this.lastUid = uid.uid;
      const block = parseReadSecLine(line);
      if (block) this.applyReadBlock(block);
    },
    applyReadBlock(block) {
      if (block.sector === 1 && block.block === 0) {
        const parsed = parseStoredIndex(block.hex);
        const next = {
          status: parsed.plan ? "data" : "blank",
          indexHex: parsed.hex,
          indexText: parsed.text,
          index: parsed.plan
            ? parsed.plan.map((item, index) => ({
              field: ["phone", "cardNo", "balance", "points"][index],
              sector: item.sector,
              block: item.block,
              text: "",
              hex: "",
            }))
            : [],
          balance: "",
          currency: "",
        };
        this.cardInfo = next;
        return;
      }
      if (!this.cardInfo || !this.cardInfo.index) return;
      const row = this.cardInfo.index.find((item) => item.sector === block.sector && item.block === block.block);
      if (!row) return;
      row.hex = block.hex;
      row.text = hexToCardText(block.hex);
      if (row.field === "balance") {
        const parts = row.text.split("|");
        this.cardInfo.balance = parts[0] || "";
        this.cardInfo.currency = parts[1] || "";
      }
    },
    send(payload) {
      const line = String(payload || this.command || "").trim();
      if (!line) return;
      if (!this.ready) {
        this.$message.warning(this.$t("menuitems.clouds.nfcreader.rfid.needDevice"));
        return;
      }
      const sent = sendMessage(JSON.stringify({ type: "command", command: "write", payload: line }));
      if (!sent) {
        this.$message.warning(this.$t("menuitems.clouds.nfcreader.serviceOffline"));
        return;
      }
      this.lines.push(new Date().toLocaleTimeString() + "  > " + line);
      if (!payload) this.command = "";
    },
  },
};
</script>

<style scoped>
.reader-debug__alert {
  margin-bottom: 12px;
}
.reader-debug__bar,
.reader-debug__send {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.reader-debug__send .el-input {
  width: 360px;
  max-width: 100%;
}
.reader-debug__card {
  margin-bottom: 12px;
}
.reader-debug__index-title {
  font-weight: 600;
  margin-bottom: 6px;
}
.reader-debug__empty {
  margin-top: 8px;
  color: #f1b44c;
}
.reader-debug__log {
  min-height: 220px;
  max-height: 420px;
  overflow: auto;
  margin: 0;
  padding: 12px;
  background: #1e1e1e;
  color: #d4d4d4;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
